import React, { useMemo } from 'react';

import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { getAtlassianStudioAgentEditUrl } from '@atlassian/conversation-assistant-agent/common/utils/atlassian-studio-url';

import { STUDIO_NAVIGATION_SOURCE } from '../constants';
import { getAgentIdFromAri } from '../utils';

import type { CreateAgentCardWithQueryAgentByIdQuery } from './__generated__/CreateAgentCardWithQueryAgentByIdQuery.graphql';
import { CreateAgentCard } from './CreateAgentCard';

type CreateAgentCardWithQueryProps = Omit<
	React.ComponentProps<typeof CreateAgentCard>,
	'isPublished'
> & {
	renderPrimaryAction?: ({ isPublished }: { isPublished: boolean }) => React.ReactNode;
	renderSecondaryAction?: ({ isPublished }: { isPublished: boolean }) => React.ReactNode;
};

const mapKnowledgeFilters = (
	filters:
		| {
				readonly spaceFilter?: ReadonlyArray<string> | null;
				readonly parentFilter?: ReadonlyArray<string> | null;
				readonly projectFilter?: ReadonlyArray<string> | null;
		  }
		| null
		| undefined,
) => ({
	spaceFilter: filters?.spaceFilter ? [...filters.spaceFilter] : undefined,
	parentFilter: filters?.parentFilter ? [...filters.parentFilter] : undefined,
	projectFilter: filters?.projectFilter ? [...filters.projectFilter] : undefined,
});

export const CreateAgentCardWithQuery = ({
	agentAri = '',
	cloudId,
	agentName,
	description,
	instructions,
	imageUrl,
	agentIdentityAccountId,
	agentNamedId,
	knowledge: planKnowledge,
	tools: planTools,
	primaryAction,
	secondaryAction,
	renderExperienceName,
	shouldRenderDetailedPublishedCard,
	renderPrimaryAction,
	renderSecondaryAction,
}: CreateAgentCardWithQueryProps): React.JSX.Element => {
	di();

	const data = useLazyLoadQuery<CreateAgentCardWithQueryAgentByIdQuery>(
		graphql`
			query CreateAgentCardWithQueryAgentByIdQuery($agentAri: ID!, $cloudId: String!) {
				agentStudio_hasVersionCapability(cloudId: $cloudId) @optIn(to: "AgentStudio") {
					__typename
					... on AgentStudioHasVersionCapability {
						hasVersionCapability
					}
				}
				agentStudio_agentById(id: $agentAri) @optIn(to: "AgentStudio") @required(action: THROW) {
					__typename
					... on AgentStudioAssistant {
						id @required(action: THROW)
						name
						description
						instructions
						icon
						identityAccountId
						externalConfigReference
						isPublished
						tools {
							id
							displayName
							description
							iconUrl
							tags
						}
						knowledgeSources {
							enabled
							sources {
								enabled
								source
								filters {
									... on AgentStudioConfluenceKnowledgeFilter {
										spaceFilter
										parentFilter
									}
									... on AgentStudioJiraKnowledgeFilter {
										projectFilter
									}
								}
							}
						}
					}
				}
			}
		`,
		{ agentAri, cloudId: cloudId ?? '' },
		{ fetchPolicy: 'store-or-network' },
	);

	const hasVersionCapability = useMemo(
		() =>
			data.agentStudio_hasVersionCapability?.__typename === 'AgentStudioHasVersionCapability'
				? Boolean(data.agentStudio_hasVersionCapability.hasVersionCapability)
				: false, // this is consistent with other similar usage patterns for this query
		[data.agentStudio_hasVersionCapability],
	);

	const agent = useMemo(
		() =>
			data.agentStudio_agentById?.__typename === 'AgentStudioAssistant'
				? data.agentStudio_agentById
				: null,
		[data.agentStudio_agentById],
	);

	const tools = useMemo(
		() =>
			(agent?.tools ?? []).map((tool) => ({
				id: tool.id,
				displayName: tool.displayName ?? tool.id,
				tags: tool.tags ? [...tool.tags] : undefined,
				iconUrl: tool.iconUrl ?? undefined,
				isActive: true,
			})),
		[agent?.tools],
	);

	const knowledge = useMemo(() => {
		if (!agent?.knowledgeSources) return [];
		return [
			{
				enabled: agent.knowledgeSources.enabled ?? undefined,
				sources: (agent.knowledgeSources.sources ?? []).map((src) => ({
					enabled: src.enabled ?? undefined,
					source: src.source,
					filters: mapKnowledgeFilters(src.filters),
				})),
			},
		];
	}, [agent?.knowledgeSources]);

	const agentEditUrl = useMemo(() => {
		if (!cloudId || !agentAri) return undefined;
		const agentId = getAgentIdFromAri(agentAri);
		if (!agentId) return undefined;
		return getAtlassianStudioAgentEditUrl(cloudId, agentId, STUDIO_NAVIGATION_SOURCE);
	}, [cloudId, agentAri]);

	const isPublished = !!agentAri && (!hasVersionCapability || agent?.isPublished === true);

	return (
		<CreateAgentCard
			isPublished={isPublished}
			agentAri={agentAri}
			cloudId={cloudId}
			agentName={agent?.name ?? agentName}
			description={agent?.description ?? description}
			instructions={agent?.instructions ?? instructions}
			imageUrl={agent?.icon ?? imageUrl}
			agentIdentityAccountId={agent?.identityAccountId ?? agentIdentityAccountId}
			agentNamedId={agent?.externalConfigReference ?? agentNamedId}
			knowledge={agent ? knowledge : planKnowledge}
			tools={agent ? tools : planTools}
			primaryAction={renderPrimaryAction?.({ isPublished }) ?? primaryAction}
			secondaryAction={renderSecondaryAction?.({ isPublished }) ?? secondaryAction}
			agentEditUrl={agentEditUrl}
			renderExperienceName={renderExperienceName}
			shouldRenderDetailedPublishedCard={shouldRenderDetailedPublishedCard}
		/>
	);
};
